/**
 * Las dos reglas de maquetacion del modo pagina.
 *
 * <p>Viven sueltas y no dentro de {@code SessionPage} porque las dos son decisiones —no dibujo— y
 * asi se pueden probar sin montar la pagina entera.
 */
import type { UiState } from './uiState';

/**
 * Si se pinta la tarjeta de resumen del cobro.
 *
 * <p><strong>Solo mientras el comprador ELIGE como pagar.</strong> Es la misma regla que ya seguian
 * las promociones, y por el mismo motivo: el resumen ayuda a decidir. Una vez decidido —mientras se
 * procesa, mientras esta en la pasarela, o ya con el desenlace en pantalla— repetirle el detalle no
 * le aporta nada y compite con lo unico que importa en ese momento.
 *
 * <p>Se reporto mirando QA: el detalle seguia a la derecha durante la espera del pago.
 */
export function pintaResumen(uiState: UiState, hayIntent: boolean): boolean {
  return hayIntent && uiState === 'select_method';
}

/** Rejilla con las dos columnas: el contenido a la izquierda, resumen y promociones a la derecha. */
const REJILLA_DOS_COLUMNAS =
  'grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]';

/**
 * Sin columna derecha no hay rejilla: una sola columna centrada.
 *
 * <p>El defecto que esto corrige: la rejilla de dos columnas declaraba su pista de 20rem SIEMPRE,
 * tambien en las pantallas de desenlace, donde no hay ni resumen ni promociones. La pista vacia se
 * seguia reservando dentro de un panel de 62rem, asi que la tarjeta del desenlace quedaba pegada a
 * la izquierda con un vacio enorme al lado.
 */
const COLUMNA_UNICA_CENTRADA = 'flex justify-center';

export function claseDeRejilla(hayColumnaDerecha: boolean): string {
  return hayColumnaDerecha ? REJILLA_DOS_COLUMNAS : COLUMNA_UNICA_CENTRADA;
}

/** La columna del pago dentro de la rejilla de dos: ver el comentario de PAGE_MAIN_CLASS. */
const COLUMNA_EN_REJILLA =
  'flex min-w-0 flex-col gap-4 lg:col-start-1 lg:row-start-1 lg:row-span-2 ' +
  'lg:self-stretch [&>*:last-child]:grow';

/**
 * La columna del pago cuando esta sola.
 *
 * <p>El ancho maximo NO es decoracion: el panel mide 62rem, y un contenedor flex centrado deja que
 * su hijo se estire hasta ocuparlo entero. Una tarjeta de desenlace de 62rem de ancho con un check
 * y dos lineas de texto se lee como un error de maquetacion. `max-w-md` es el mismo ancho que ya
 * tiene el checkout empotrado, asi que las dos formas de verlo coinciden.
 */
const COLUMNA_SOLA = 'flex w-full min-w-0 max-w-md flex-col gap-4';

export function claseDeColumnaPrincipal(hayColumnaDerecha: boolean): string {
  return hayColumnaDerecha ? COLUMNA_EN_REJILLA : COLUMNA_SOLA;
}
